import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { ATTENDANCE_TODAY } from "@/constants/testIds";

const ESTADO_LABELS = {
  a_tiempo: { label: "A tiempo", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  retardo: { label: "Retardo", className: "bg-amber-50 text-amber-700 border-amber-200" },
  ausencia: { label: "Ausencia", className: "bg-red-50 text-red-700 border-red-200" },
  vacaciones: { label: "De vacaciones", className: "bg-petroleum/5 text-petroleum border-petroleum/20" },
  pendiente: { label: "Aún sin marcar", className: "bg-accent text-charcoal/60 border-petroleum/10" },
  no_laborable: { label: "Día no laborable", className: "bg-accent text-charcoal/60 border-petroleum/10" },
};

// Destino de la tarjeta de puntualidad del Dashboard: quién llegó a tiempo, con retardo, no
// marcó entrada (ausencia, ya pasada la tolerancia y sin estar de vacaciones aprobadas hoy), y
// quién ya marcó su salida. Un empleado que no marcó nada aparece con 0 en ambas horas.
export default function AttendanceToday() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/attendance-today").then(({ data }) => setData(data));
  }, []);

  const needsConfig = data && !data.expected_checkin_time;

  return (
    <AdminLayout>
      <div className="max-w-4xl" data-testid={ATTENDANCE_TODAY.page}>
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight flex items-center gap-3">
          <Clock className="w-7 h-7 text-terracotta" strokeWidth={1.5} /> Puntualidad de hoy
        </h1>
        <p className="text-charcoal/60 mt-1">
          {data ? `${data.date} · tolerancia de ${data.tolerance_minutes} minuto(s)` : "Cargando..."}
        </p>

        {needsConfig && (
          <div className="mt-6 bg-card border border-terracotta/30 rounded-xl p-5 text-sm text-charcoal/80">
            Para ver quién llegó a tiempo o con retardo, primero configura la hora de entrada
            esperada de tu empresa en{" "}
            <Link to="/admin/perfil-empresa" className="text-terracotta font-semibold underline">
              Perfil de Empresa
            </Link>
            .
          </div>
        )}

        {data && !data.is_workday && (
          <div className="mt-6 bg-card border border-petroleum/10 rounded-xl p-5 text-sm text-charcoal/60">
            Hoy no es un día laborable según el horario de tu empresa.
          </div>
        )}

        <div className="space-y-2 mt-6">
          {data?.employees.map((e) => {
            const info = ESTADO_LABELS[e.estado] || ESTADO_LABELS.pendiente;
            return (
              <div
                key={e.employee_id}
                data-testid={ATTENDANCE_TODAY.row}
                className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3"
              >
                <div>
                  <p className="font-semibold text-charcoal">{e.full_name}</p>
                  <p className="text-sm text-charcoal/60">{e.position || "—"}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <p className="text-charcoal/50 text-xs">Entrada</p>
                    <p className="font-medium text-charcoal">{e.entrada_time || "0"}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-charcoal/50 text-xs">Salida</p>
                    <p className="font-medium text-charcoal">{e.salida_time || "0"}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${info.className}`}>
                    {info.label}
                  </span>
                </div>
              </div>
            );
          })}
          {data && data.employees.length === 0 && (
            <p data-testid={ATTENDANCE_TODAY.emptyState} className="text-charcoal/50 text-sm">
              No hay empleados activos para mostrar.
            </p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
